import { apiClient } from '../utils/api-client';
import {
  ApiFundMetrics,
  ApiFundMetricsSeries,
  Fund,
  FundFigure,
  FundMetrics,
  FundMetricsSeries,
  FundSummary,
  FundOption,
  FundCreate,
  FundUpdate,
  FundBankAccount,
  FundEntity,
  FundEmailBatchSummary,
  FundEmailBatchDetailResponse,
  FundPortalPublication,
  FundPortalPublicationUpdate,
  MetricsReleaseQueueItem,
  MetricsReleaseAction,
  FinalizeCloseRequest,
} from '../types/fund';
import {
  FundLPATerms,
  FundLPATermsCreate,
  FundLPATermsUpdate,
  FeeBasisType,
  WaterfallType,
} from '../types/lpa-terms';
import { InvestorDetail } from '@/types/investor';
import { ConvertibleInstrumentFundDetail } from '@/types/captable';
import { DEFAULT_LEDGER_VIEW, type LedgerView } from '@/types/drawdown';
import {
  mapFundMetrics,
  mapFundMetricsSeries,
  mapFund,
  mapFundSummary,
  mapConvertibleInstrumentFundDetail,
} from './fundMapper';

export const fundService = {
  // Get all funds
  async getFunds(params?: {
    skip?: number;
    limit?: number;
    fund_type?: string;
    status?: string;
    include_archived?: boolean;
  }): Promise<FundSummary[]> {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.append('skip', params.skip.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.fund_type) searchParams.append('fund_type', params.fund_type);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.include_archived !== undefined)
      searchParams.append(
        'include_archived',
        params.include_archived.toString()
      );

    const funds = await apiClient.get<FundSummary[]>(
      `/funds?${searchParams.toString()}`
    );
    return funds.map(mapFundSummary);
  },

  // Get fund statistics
  async getFundStatistics(): Promise<{
    total_funds: number;
    total_commitments: number;
    status_breakdown: Record<string, number>;
    active_funds: number;
  }> {
    return await apiClient.get('/funds/statistics');
  },

  // Get fund options for dropdowns (minimal data)
  async getFundOptions(params?: {
    include_archived?: boolean;
  }): Promise<FundOption[]> {
    const searchParams = new URLSearchParams();
    if (params?.include_archived !== undefined)
      searchParams.append(
        'include_archived',
        params.include_archived.toString()
      );

    return await apiClient.get(`/funds/options?${searchParams.toString()}`);
  },

  // Get fund performance metrics
  async getFundMetrics(
    fundId: string,
    view: LedgerView = DEFAULT_LEDGER_VIEW,
    asOf?: string
  ): Promise<FundMetrics> {
    const searchParams = new URLSearchParams({ view });
    if (asOf) searchParams.set('as_of', asOf);
    const raw = await apiClient.get<ApiFundMetrics>(
      `/funds/${fundId}/metrics?${searchParams}`
    );
    return mapFundMetrics(raw);
  },

  async getFundMetricsSeries(
    fundId: string,
    view: LedgerView = DEFAULT_LEDGER_VIEW,
    from?: string,
    to?: string
  ): Promise<FundMetricsSeries> {
    const searchParams = new URLSearchParams({ view });
    if (from) searchParams.set('from', from);
    if (to) searchParams.set('to', to);
    const raw = await apiClient.get<ApiFundMetricsSeries>(
      `/funds/${fundId}/metrics/series?${searchParams}`
    );
    return mapFundMetricsSeries(raw);
  },

  async getFundFigures(): Promise<FundFigure[]> {
    return apiClient.get<FundFigure[]>('/funds/metrics/figures');
  },

  // Get a specific fund
  async getFund(fundId: string, includeBankAccounts?: boolean): Promise<Fund> {
    const params = includeBankAccounts ? '?include_bank_accounts=true' : '';
    const raw = await apiClient.get<Fund>(`/funds/${fundId}${params}`);
    return mapFund(raw);
  },

  // Create a new fund
  async createFund(fundData: FundCreate): Promise<Fund> {
    const raw = await apiClient.post<Fund>('/funds', fundData);
    return mapFund(raw);
  },

  // Update a fund
  async updateFund(fundId: string, fundData: FundUpdate): Promise<Fund> {
    const raw = await apiClient.put<Fund>(`/funds/${fundId}`, fundData);
    return mapFund(raw);
  },

  // Delete a fund
  async deleteFund(fundId: string): Promise<void> {
    await apiClient.delete(`/funds/${fundId}`);
  },

  // Archive a fund
  async archiveFund(fundId: string): Promise<void> {
    await apiClient.post(`/funds/${fundId}/archive`);
  },

  // Unarchive a fund
  async unarchiveFund(fundId: string): Promise<void> {
    await apiClient.post(`/funds/${fundId}/unarchive`);
  },

  // Get investor-portal publication settings for a fund
  async getFundPortalPublication(
    fundId: string
  ): Promise<FundPortalPublication> {
    return await apiClient.get<FundPortalPublication>(
      `/funds/${fundId}/portal-publication`
    );
  },

  // Update investor-portal publication settings for a fund
  async updateFundPortalPublication(
    fundId: string,
    data: FundPortalPublicationUpdate
  ): Promise<FundPortalPublication> {
    return await apiClient.put<FundPortalPublication>(
      `/funds/${fundId}/portal-publication`,
      data
    );
  },

  // ── Fund-metrics period release ──────────────────────────────────────────

  // Funds with a finalized-but-unreleased metrics period, awaiting confirmation
  async getMetricsReleaseQueue(): Promise<MetricsReleaseQueueItem[]> {
    return await apiClient.get<MetricsReleaseQueueItem[]>(
      '/funds/metrics-release-queue'
    );
  },

  // Finalize a fund's period close (queues or auto-releases its metrics)
  async finalizeMetricsClose(
    fundId: string,
    data: FinalizeCloseRequest
  ): Promise<FundPortalPublication> {
    return await apiClient.post<FundPortalPublication>(
      `/funds/${fundId}/metrics-close/finalize`,
      data
    );
  },

  // Toggle per-fund auto-release of metrics on close
  async updateMetricsAutoRelease(
    fundId: string,
    autoRelease: boolean
  ): Promise<FundPortalPublication> {
    return await apiClient.put<FundPortalPublication>(
      `/funds/${fundId}/metrics-auto-release`,
      { auto_release: autoRelease }
    );
  },

  // Release queued metrics (empty fund_ids = all queued funds)
  async confirmMetricsRelease(
    data: MetricsReleaseAction
  ): Promise<FundPortalPublication[]> {
    return await apiClient.post<FundPortalPublication[]>(
      '/funds/metrics-release/confirm',
      data
    );
  },

  // Revoke (pull back) the visible metrics period for the given funds
  async revokeMetricsRelease(
    data: MetricsReleaseAction
  ): Promise<FundPortalPublication[]> {
    return await apiClient.post<FundPortalPublication[]>(
      '/funds/metrics-release/revoke',
      data
    );
  },

  // Get bank accounts for a fund
  async getFundBankAccounts(
    fundId: string,
    activeOnly?: boolean
  ): Promise<FundBankAccount[]> {
    const params = activeOnly !== undefined ? `?active_only=${activeOnly}` : '';
    return await apiClient.get(`/funds/${fundId}/bank-accounts${params}`);
  },

  // Create a bank account for a fund
  async createBankAccount(
    fundId: string,
    accountData: Omit<
      FundBankAccount,
      'id' | 'fund_id' | 'created_at' | 'updated_at'
    >
  ): Promise<FundBankAccount> {
    return await apiClient.post(`/funds/${fundId}/bank-accounts`, accountData);
  },

  // Update a bank account
  async updateBankAccount(
    fundId: string,
    accountId: string,
    accountData: Partial<
      Omit<FundBankAccount, 'id' | 'fund_id' | 'created_at' | 'updated_at'>
    >
  ): Promise<FundBankAccount> {
    return await apiClient.put(
      `/funds/${fundId}/bank-accounts/${accountId}`,
      accountData
    );
  },

  // Set primary bank account
  async setPrimaryBankAccount(
    fundId: string,
    accountId: string
  ): Promise<void> {
    await apiClient.post(
      `/funds/${fundId}/bank-accounts/${accountId}/set-primary`
    );
  },

  // Deactivate a bank account
  async deactivateBankAccount(
    fundId: string,
    accountId: string
  ): Promise<void> {
    await apiClient.post(
      `/funds/${fundId}/bank-accounts/${accountId}/deactivate`
    );
  },

  // Get fund entities for a fund
  async getFundEntities(fundId: string): Promise<FundEntity[]> {
    return await apiClient.get(`/funds/${fundId}/entities`);
  },

  // Create a fund entity for a fund
  async createFundEntity(
    fundId: string,
    entityData: Omit<FundEntity, 'id' | 'fund_id' | 'created_at' | 'updated_at'>
  ): Promise<FundEntity> {
    return await apiClient.post(`/funds/${fundId}/entities`, entityData);
  },

  // Update a fund entity
  async updateFundEntity(
    fundId: string,
    entityId: string,
    entityData: Partial<
      Omit<FundEntity, 'id' | 'fund_id' | 'created_at' | 'updated_at'>
    >
  ): Promise<FundEntity> {
    return await apiClient.put(
      `/funds/${fundId}/entities/${entityId}`,
      entityData
    );
  },

  // Delete a fund entity
  async deleteFundEntity(fundId: string, entityId: string): Promise<void> {
    await apiClient.delete(`/funds/${fundId}/entities/${entityId}`);
  },

  // Get fund investors
  async getFundInvestors(fundId: string): Promise<InvestorDetail[]> {
    return await apiClient.get(`/investors/funds/${fundId}/investors`);
  },

  // Add investor to fund
  async addInvestorToFund(
    fundId: string,
    investorId: string,
    commitmentAmount: string
  ): Promise<void> {
    await apiClient.post(`/funds/${fundId}/investors`, {
      investor_id: investorId,
      commitment_amount: commitmentAmount,
    });
  },

  // Remove investor from fund
  async removeInvestorFromFund(
    fundId: string,
    investorId: string
  ): Promise<void> {
    await apiClient.delete(`/funds/${fundId}/investors/${investorId}`);
  },

  // Email fund investors
  async emailFundInvestors(
    fundId: string,
    emailData: {
      subject: string;
      html_content: string;
      text_content?: string;
      targets: Array<{ investor_id: string; contact_id?: string }>;
    }
  ): Promise<void> {
    return await apiClient.post(
      `/funds/${fundId}/investor-emails/send`,
      emailData
    );
  },

  // Preview email for investor
  async previewFundEmail(
    fundId: string,
    investorId: string,
    contactId?: string,
    templateId?: string,
    subject?: string,
    htmlContent?: string
  ): Promise<{ subject: string; html: string; text: string }> {
    return await apiClient.post(`/funds/${fundId}/investor-emails/preview`, {
      template_id: templateId,
      investor_id: investorId,
      contact_id: contactId,
      subject: subject,
      html_content: htmlContent,
    });
  },

  // Get email history for a fund
  async getFundEmails(
    fundId: string,
    params?: { skip?: number; limit?: number }
  ): Promise<FundEmailBatchSummary[]> {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.append('skip', params.skip.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    return await apiClient.get(
      `/funds/${fundId}/investor-emails?${searchParams.toString()}`
    );
  },

  // Get details for a specific email batch
  async getFundEmailBatchDetails(
    fundId: string,
    batchId: string
  ): Promise<FundEmailBatchDetailResponse> {
    return await apiClient.get(`/funds/${fundId}/investor-emails/${batchId}`);
  },

  // Get convertible instruments for a fund
  async getFundConvertibles(
    fundId: string,
    outstandingOnly: boolean = true
  ): Promise<ConvertibleInstrumentFundDetail[]> {
    const raw = await apiClient.get<ConvertibleInstrumentFundDetail[]>(
      `/funds/${fundId}/convertibles?outstanding_only=${outstandingOnly}`
    );
    return raw.map(mapConvertibleInstrumentFundDetail);
  },

  // === LPA Terms API Methods ===

  // Get LPA terms for a fund
  async getFundLPATerms(fundId: string): Promise<FundLPATerms> {
    return await apiClient.get(`/funds/${fundId}/lpa-terms`);
  },

  // Create LPA terms for a fund
  async createFundLPATerms(
    lpaTermsData: FundLPATermsCreate
  ): Promise<FundLPATerms> {
    return await apiClient.post(
      `/funds/${lpaTermsData.fund_id}/lpa-terms`,
      lpaTermsData
    );
  },

  // Update LPA terms for a fund
  async updateFundLPATerms(
    fundId: string,
    lpaTermsData: FundLPATermsUpdate
  ): Promise<FundLPATerms> {
    return await apiClient.put(`/funds/${fundId}/lpa-terms`, lpaTermsData);
  },

  // Delete LPA terms for a fund
  async deleteFundLPATerms(fundId: string): Promise<void> {
    await apiClient.delete(`/funds/${fundId}/lpa-terms`);
  },

  // === LPA Types API Methods ===

  // Get all fee basis types
  async getFeeBasisTypes(): Promise<FeeBasisType[]> {
    return await apiClient.get('/funds/lpa-types/fee-basis-types');
  },

  // Get active fee basis types only
  async getActiveFeeBasisTypes(): Promise<FeeBasisType[]> {
    return await apiClient.get(
      '/funds/lpa-types/fee-basis-types?active_only=true'
    );
  },

  // Get all waterfall types
  async getWaterfallTypes(): Promise<WaterfallType[]> {
    return await apiClient.get('/funds/lpa-types/waterfall-types');
  },

  // Get active waterfall types only
  async getActiveWaterfallTypes(): Promise<WaterfallType[]> {
    return await apiClient.get(
      '/funds/lpa-types/waterfall-types?active_only=true'
    );
  },

  // === Distribution Waterfall ===

  // Get auto-computed distribution waterfall for a fund
  async getDistributionWaterfall(
    fundId: string,
    type: 'realised' | 'unrealised' = 'realised'
  ): Promise<unknown> {
    const endpoint =
      type === 'unrealised'
        ? `/funds/${fundId}/waterfall/unrealised`
        : `/funds/${fundId}/distribution-waterfall`;
    return await apiClient.get(endpoint);
  },
};
